import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Tabs from '../components/Tabs';
import FamilyList from '../components/families/FamilyList';
import FamilyDetail from '../components/families/FamilyDetail';
import InvoiceList from '../components/families/InvoiceList';
import TransactionList from '../components/families/TransactionList';
import NewInvoiceDrawer from '../components/families/NewInvoiceDrawer';
import AddTransactionDrawer from '../components/families/AddTransactionDrawer';
import InvoiceDocument from '../components/families/InvoiceDocument';
import ChargeCategoriesDrawer from '../components/families/ChargeCategoriesDrawer';
import SendInvoiceDrawer from '../components/families/SendInvoiceDrawer';
import AddStudentDrawer from '../components/students/AddStudentDrawer';
import { useFamilies } from '../data/useFamilies';
import { useLedger } from '../data/useLedger';
import { useStudents } from '../data/useStudents';
import { useInvoices } from '../data/useInvoices';
import { useSettings } from '../data/useSettings';
import useInvoiceLineDetail from '../hooks/useInvoiceLineDetail';
import useDocumentLink from '../hooks/useDocumentLink';
import useInvoiceLogo from '../hooks/useInvoiceLogo';
import useSendInvoice from '../hooks/useSendInvoice';
import { useNotifications } from '../data/useNotifications';
import { useAuth } from '../context/AuthContext';
import { composeFromChoices } from '../domain/notifications';
import { frozenBodyOf } from '../domain/documentRender';
import { familyDisplayName, teacherDisplayName, teacherNameParts } from '../domain/people';
import { monthRange, transactionsInRange } from '../domain/familyAccount';
import { todayISO } from '../domain/clock';
import s from '../components/families/families.module.css';

// Families & Invoices. Three tabs, matching MMS: the family accounts with their
// balances, the invoices, and a cross-family transaction log.
//
// Assembly only — each tab is its own component, and so is each drawer.

const TABS = ['Family Accounts', 'Invoices', 'Transactions'];

// The current month, on the UTC calendar grid: `new Date(y, m, 1)` is local
// midnight, and `.toISOString()` would land it a day early east of Greenwich.
// `monthRange` (src/domain/familyAccount.js) builds both ends from today's
// date with utcDate(), which never converts.

export default function FamiliesPage() {
  const {
    families, loading, failed: familiesFailed, contactsLoading, contactsFailed,
    removeFamily, contactsFor, billingContactFor,
  } = useFamilies();
  // The money lives in the ledger, not on the family: balances are folded from
  // the entries rather than stored, so every figure here comes from useLedger.
  const {
    balanceFor, prepaidFor, totalOwed, ledgerFor, entries, voidEntry,
    loading: ledgerLoading, failed: ledgerFailed,
  } = useLedger();
  const { forFamily, loading: studentsLoading, failed: studentsFailed } = useStudents();
  const {
    invoices, pendingRequests, failedRequests, skippedRows, notEmailed,
    loading: invoicesLoading, requestsLoading, failed: invoicesFailed,
  } = useInvoices();
  // Which reads have answered (#629): every list and figure below says
  // nothing about one that has not — `reading`, src/domain/reading.js.
  const reads = useMemo(() => ({
    families: !loading,
    students: !studentsLoading,
    contacts: !contactsLoading,
    ledger: !ledgerLoading,
    invoices: !invoicesLoading && !requestsLoading,
  }), [loading, studentsLoading, contactsLoading, ledgerLoading, invoicesLoading, requestsLoading]);
  // And which stopped before they answered (#647): those say so, with Try
  // again, where the loading state would otherwise stand for good.
  const failures = useMemo(() => ({
    families: familiesFailed,
    students: studentsFailed,
    contacts: contactsFailed,
    ledger: ledgerFailed,
    invoices: invoicesFailed,
  }), [familiesFailed, studentsFailed, contactsFailed, ledgerFailed, invoicesFailed]);
  // What the invoice heads itself with. Falls back to "Your Studio" inside
  // useSettings for a studio that has not named itself.
  //
  // `invoicing` is Business Settings → Accounts & Invoices: the invoice name,
  // footer, accent colour, how a credit balance reads, and whether charges are
  // grouped by student or carry their location. InvoiceDocument applies them.
  const { name: studioName, settings } = useSettings();
  // The invoice's Open PDF (#621): the server's PDF of it, by link.
  const documentLink = useDocumentLink();

  // /families and /invoices render the same page; the path decides which tab it
  // opens on, so the Invoices nav entry does not dump you on Family Accounts.
  const { pathname } = useLocation();
  const [tab, setTab] = useState(() => (pathname.startsWith('/invoices') ? 1 : 0));
  const [openFamilyId, setOpenFamilyId] = useState(null);
  // null | 'transaction' | 'invoice' | 'categories' | 'student'. The last is
  // the Add Student wizard, the empty tabs' way forward (#630).
  const [openDrawer, setOpenDrawer] = useState(null);
  const [openInvoiceId, setOpenInvoiceId] = useState(null);
  const [range, setRange] = useState(() => monthRange(todayISO()));

  const totalPrepaid = useMemo(
    () => families.reduce((acc, f) => acc + prepaidFor(f.id), 0),
    [families, prepaidFor]
  );
  // Over the same families as the rows and Prepaid Balance, so the header is
  // what the rows under it add up to (#640).
  const owed = useMemo(() => totalOwed(families.map((f) => f.id)), [families, totalOwed]);

  // Live entries only — a tombstone is two fields and only the domain
  // predicate knows both (issue #70) — in the range, newest first.
  const txRows = useMemo(() => transactionsInRange(entries, range), [entries, range]);

  const family = openFamilyId ? families.find((f) => f.id === openFamilyId) : null;
  const openInvoice = invoices.find((i) => i.id === openInvoiceId) || null;
  // Student and location per line, for the two settings an invoice that is not
  // frozen cannot answer on its own (hooks/useInvoiceLineDetail.js). A frozen
  // invoice (#622) carries its own.
  const lineDetail = useInvoiceLineDetail(frozenBodyOf(openInvoice) ? null : openInvoice);
  // The logo it was drawn with (#622, hooks/useInvoiceLogo.js).
  const invoiceLogo = useInvoiceLogo(openInvoice, settings.logo || null);

  // An invoice row's Email (#622): MMS's Send Invoice drawer. From is the
  // composer's (#600) — the studio's address or the teacher's — and BCC me
  // copies the teacher at their sign-in address; the message is queued on
  // the outbox as New Message's is.
  const { user } = useAuth();
  const { sendTeacherMessage } = useNotifications();
  // The teacher's choice is labelled with the name it is sent under: the
  // Teacher Name Format's (#797, deliver.js `namedTeacher`).
  const teacherName = teacherDisplayName(settings, user?.displayName);
  const teacherParts = teacherNameParts(settings, user?.displayName);
  const fromChoices = useMemo(() => composeFromChoices({
    studioName,
    studioEmail: settings?.messaging?.studioEmail,
    teacherName,
    teacherEmail: user?.email,
  }), [studioName, settings?.messaging?.studioEmail, teacherName, user?.email]);
  const familyFor = useCallback((id) => families.find((f) => f.id === id) || null, [families]);
  const studentIdsFor = useCallback((id) => forFamily(id).map((st) => st.id), [forFamily]);
  const invoiceSender = useSendInvoice({
    send: sendTeacherMessage,
    myEmail: user?.email || null,
    fromChoices,
    studioName,
    teacherName,
    teacherFirstName: teacherParts.firstName,
    teacherLastName: teacherParts.lastName,
    studioLocale: settings.locale || null,
    familyFor,
    contactsFor,
    billingContactFor,
    studentIdsFor,
  });
  const closeDrawer = () => setOpenDrawer(null);

  return (
    <AppShell title="Families & Invoices">
      <div className={s.page}>
        <Tabs
          tabs={TABS}
          current={tab}
          onChange={(i) => { setTab(i); setOpenFamilyId(null); }}
        />

        {tab === 0 && !family ? (
          <FamilyList
            families={families}
            reads={reads}
            failures={failures}
            totalOwed={owed}
            totalPrepaid={totalPrepaid}
            balanceFor={balanceFor}
            studentCountFor={(id) => forFamily(id).length}
            billingContactFor={billingContactFor}
            onOpenFamily={setOpenFamilyId}
            onChargeCategories={() => setOpenDrawer('categories')}
            onAddStudent={() => setOpenDrawer('student')}
          />
        ) : null}

        {tab === 0 && family ? (
          <FamilyDetail
            family={{ ...family, balanceMinor: balanceFor(family.id) }}
            students={forFamily(family.id)}
            // The family's contacts (#358): the account card draws the same
            // people the student's Family Contacts tab does.
            contacts={contactsFor(family.id)}
            // Which of them the server will bill (#319), resolved here so the
            // card and the invoice cannot pick differently.
            billingContact={billingContactFor(family.id)}
            ledger={ledgerFor(family.id)}
            reads={reads}
            failures={failures}
            onBack={() => setOpenFamilyId(null)}
            onAddTransaction={() => setOpenDrawer('transaction')}
            onVoidEntry={voidEntry}
            // Back to the list on a delete that went through, because the
            // screen behind the dialog is a family that no longer exists
            // (#346). A refused delete returns a plan and stays put; the dialog
            // renders the refusal.
            onDeleteFamily={async (id) => {
              const plan = await removeFamily(id);
              if (plan?.deletable) setOpenFamilyId(null);
              return plan;
            }}
          />
        ) : null}

        {tab === 1 ? (
          <InvoiceList
            invoices={invoices}
            pendingRequests={pendingRequests}
            failedRequests={failedRequests}
            skippedRows={skippedRows}
            read={reads.invoices}
            failed={failures.invoices}
            families={families}
            onNewInvoice={() => setOpenDrawer('invoice')}
            onOpenInvoice={setOpenInvoiceId}
            onEmailInvoice={invoiceSender.open}
            notEmailed={notEmailed}
            onAddStudent={() => setOpenDrawer('student')}
          />
        ) : null}

        {tab === 2 ? (
          <TransactionList
            rows={txRows}
            read={reads.ledger}
            failed={failures.ledger}
            families={families}
            range={range}
            onRangeChange={setRange}
            // `TransactionList` treats `onVoidEntry` as the switch for the whole
            // kebab column, so Delete Transaction (#191, #309) needs it.
            onVoidEntry={voidEntry}
          />
        ) : null}
      </div>

      <AddTransactionDrawer
        open={openDrawer === 'transaction'}
        onClose={closeDrawer}
        familyId={openFamilyId}
      />
      <ChargeCategoriesDrawer open={openDrawer === 'categories'} onClose={closeDrawer} />
      <SendInvoiceDrawer sender={invoiceSender} />
      <NewInvoiceDrawer open={openDrawer === 'invoice'} onClose={closeDrawer} families={families} />
      <AddStudentDrawer open={openDrawer === 'student'} onClose={closeDrawer} />
      <InvoiceDocument
        open={Boolean(openInvoice)}
        onClose={() => setOpenInvoiceId(null)}
        invoice={openInvoice}
        studioName={studioName}
        invoicing={settings.invoicing}
        locale={settings.locale || null}
        logo={invoiceLogo}
        lineDetail={lineDetail}
        documentLink={documentLink}
        familyName={openInvoice
          ? familyDisplayName(families.find((f) => f.id === openInvoice.familyId))
          : undefined}
      />
    </AppShell>
  );
}
